import { defineComponent, h, withDirectives, resolveDirective } from 'vue';
import BaseComponent from './baseFormMixin.jsx';
import variableBindingMixin from './variableBindingMixin.jsx';
import { renderVariableAssistIcon } from './variableBindingUi.jsx';
import utils from '../../../Shared/utils.jsx';
import { QCheckbox, QIcon, QField, QTooltip, QInput } from 'quasar';

import TranslationContainer from '../TranslationContainer.jsx';

/** true / false, including the strings the property grid may leave behind. Empty stays a checkbox. */
function isPureBoolean(value) {
    if (value == null || value === '')
        return true;
    if (typeof value === 'boolean')
        return true;
    if (typeof value !== 'string')
        return false;
    const t = value.trim().toLowerCase();
    return t === 'true' || t === 'false';
}

function booleanFromLiteral(value) {
    if (value === true)
        return true;
    if (typeof value === 'string' && value.trim().toLowerCase() === 'true')
        return true;
    return false;
}

export default defineComponent({
    name: 'sform-checkbox',
    mixins: [BaseComponent, variableBindingMixin],
    components: {
        TranslationContainer,
        QCheckbox,
        QIcon,
        QField,
        QTooltip,
        QInput,
    },
    data: () => ({
        readOnlyEval: null,
    }),
    props: {
    },
    created() {
    },
    mounted() {
        this.loadComplete();
    },
    computed: {
        translatedhint: function () {
            return this.Translate(this.hintText);
        },
        readOnly() {
            try {
                if (this.readOnlyEval === null && this.element?.readonly)
                    this.readOnlyEval = utils.compile(this, this.element.readonly);
                if (this.readOnlyEval) {
                    const val = utils.evaluate(this.readOnlyEval, this);
                    if (typeof val === 'boolean')
                        return val;
                    if (typeof val === 'string')
                        return val === 'true' || val === 'True';
                    return !!val;
                }
                const fallback = this.readonly ?? this.appearance?.readonly;
                if (typeof fallback === 'boolean')
                    return fallback;
                if (typeof fallback === 'string')
                    return fallback === 'true' || fallback === 'True';
                return !!fallback;
            }
            catch (e) {
                utils.warn('ReadOnly could not evaluate expression: ' + this.element?.readonly + '; ' + e);
                return false;
            }
        },
    },
    methods: {
        /**
         * Pill clear returns the previous literal, or the schema default, or false.
         * A restored expression string stays in the text input.
         */
        exitVariableBindingMode() {
            this.closeVariablePicker();
            this._vbModeOverride = 'literal';
            let restore = this._vbLiteralScratch;
            this._vbLiteralScratch = undefined;
            if (restore === undefined || restore === null || restore === '') {
                const schema = this.element?.schema;
                restore = schema && Object.prototype.hasOwnProperty.call(schema, 'default')
                    ? schema.default
                    : false;
            }
            if (isPureBoolean(restore))
                this.sync(this.storedBoolean(booleanFromLiteral(restore)));
            else
                this.sync(restore);
        },
        variableAssistIcon({ ignoreExpressionMode = false } = {}) {
            if (!this.variableAssistAllowed || this.readOnly)
                return null;
            // Expression text is not "literal chrome"; still show {} so the picker stays reachable.
            const visible = ignoreExpressionMode
                ? !!(this._vbHovered || this._vbFocused || this._vbPickerOpen)
                : this.showVariableAssistChrome;
            return renderVariableAssistIcon({
                visible,
                pickerOpen: this._vbPickerOpen,
                onOpenPicker: () => this.openVariablePicker(),
                menu: this.renderVariablePicker(),
            });
        },
        /** Advanced dropdown stores 'true' / 'false'. A real boolean schema stores booleans. */
        storedBoolean(checked) {
            const schema = this.element?.schema;
            if (schema?.formatData?.EssentialsCheckbox || schema?.type === 'string')
                return checked ? 'true' : 'false';
            return !!checked;
        },
        onExpressionInput(value) {
            if (isPureBoolean(value))
                this.sync(this.storedBoolean(booleanFromLiteral(value)));
            else
                this.sync(value);
        },
        revertExpression() {
            this.closeVariablePicker();
            this._vbModeOverride = 'literal';
            this._vbLiteralScratch = undefined;
            const schema = this.element?.schema;
            let restore = schema && Object.prototype.hasOwnProperty.call(schema, 'default')
                ? schema.default
                : false;
            if (!isPureBoolean(restore))
                restore = false;
            this.sync(this.storedBoolean(booleanFromLiteral(restore)));
        },
    },
    render() {
        // This doesn't work for unknown reason - attempt to append a tooltip icon in the checkbox label
        //let scopedSlots;
        //if (this.element.description) {
        //    const label = this.$parent.getLabel(this.element);
        //    const helpicon = utils.generateTooltip(h, <v-icon color="silver" x-small>mdi mdi-information</v-icon>, this.element.description, 'right');
        //    scopedSlots = {
        //        append: () => 'My strange label'
        //    };
        //    //delete this.appearance.label;
        //}

        // Note: Quasar QCheckbox has only a default slot for the label (no "label" or "append" slots like Vuetify).
        // Put the label and optional hint icon in the default slot.
        const defaultSlotContent = this.hintText
            ? [
                <TranslationContainer key="label" context={this} value={this.labelText}></TranslationContainer>,
                <QIcon key="hint" size="xs" class="q-pl-sm form-hint-icon" name="mdi-information">
                    <QTooltip transition-show="scale" transition-hide="scale" class="text-body2">{this.translatedhint}</QTooltip>
                </QIcon>
                //utils.generateTooltip(h, <QIcon key="hint" size="xs" class="q-pl-sm" name="mdi-information" color="grey-5"></QIcon>, this.translatedhint, 'right'),
            ]
            : <TranslationContainer context={this} value={this.labelText}></TranslationContainer>;

        if (this.vbBoundName && this.variableBindingEnabled && this.isVariableBindingMode) {
            const pill = this.tryRenderVariableBindingField({ class: 'c-formCheckbox' });
            if (pill)
                return pill;
        }

        if (!isPureBoolean(this.itemvalue)) {
            const expression = (
                <QInput
                    type="text"
                    class="c-formCheckbox caption sform-input-white"
                    outlined
                    dense
                    label={this.inputLabel}
                    style={{ width: '100%' }}
                    modelValue={this.itemvalue == null ? '' : String(this.itemvalue)}
                    rules={this.rules} {...this.compilerErrorFieldProps}
                    disable={this.readOnly || this.noinput}
                    onUpdate:modelValue={(value) => this.onExpressionInput(value)}
                    onFocus={() => { this._vbFocused = true; this.onFocus(); }}
                    onBlur={() => { this._vbFocused = false; this.onBlur(); }}
                >
                    {{
                        append: () => (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                                {this.variableAssistIcon({ ignoreExpressionMode: true })}
                                {this.readOnly ? null : (
                                    <QIcon
                                        name="mdi-close"
                                        size="xs"
                                        class="cursor-pointer"
                                        title="Revert to checkbox"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            this.revertExpression();
                                        }}
                                    >
                                        <QTooltip>Revert to checkbox</QTooltip>
                                    </QIcon>
                                )}
                            </span>
                        ),
                    }}
                </QInput>
            );
            return this.wrapLiteralWithVariableAssist(expression);
        }

        let input =
            <QField
                xhint={this.translatedhint}
                hide-hint
                borderless
                dense
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
                    <QCheckbox
                        class="caption q-pb-sm sform-checkbox-neutral"
                        dense
                        modelValue={booleanFromLiteral(this.itemvalue)}
                        rules={this.rules} {...this.compilerErrorFieldProps}
                        onUpdate:modelValue={(value) => this.sync(this.storedBoolean(value == true))}
                        onFocus={() => { this._vbFocused = true; this.onFocus(); }}
                        onBlur={() => { this._vbFocused = false; this.onBlur(); }}
                        disable={this.readOnly || this.noinput}
                    >
                        {{ default: () => defaultSlotContent }}
                    </QCheckbox>
                    {this.variableAssistIcon()}
                </div>
            </QField>;

        /*
            <br />
                {{ default: () => defaultSlotContent }}
                hide-bottom-space
                persistent-hint={appSettings.DebugTranslationPrefixSetting}

        */

        //if (this.appearance)
        //    utils.applyVue2ToVue3Props(input, this.appearance);

        //input.componentOptions.propsData = { ...input.componentOptions.propsData, ...this.appearance };

        // if (this.directives && this.directives.some(a => a.name == 'on-enter-default'))
        //    input.props.autofocus = true;

        if (this.directives) {
            var applyDirectives = this.directives.map(d => {
                const dirDef = d.directive || resolveDirective(d.name);

                return [dirDef, d.value, d.arg, d.modifiers];
            });
            input = withDirectives(input, applyDirectives);
        }

        return this.wrapLiteralWithVariableAssist(input);
    }
});